import {
  AcademicCapIcon,
  ComputerDesktopIcon,
  MinusCircleIcon,
  PaperAirplaneIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { BaseFile, IS_COMPANY_KIRK } from 'config'
import {
  AiEngine,
  AiEngineOptions,
  getAiModelOptionsForEngine,
  getAiModelsForEngine,
  resolveDefaultAiSelection,
} from 'config/ai.type'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { postAiPrompt } from 'services'
import { AiTextRender, FileLinks, InputFileAddon, TextArea, Tooltip } from 'stories/components'
import { confirm } from 'utils'

export interface IAIPromptHistory {
  role: 'user' | 'model'
  text: string
  files?: BaseFile[]
  highlight?: boolean
}

export interface IAIPromptHistoryData {
  history?: IAIPromptHistory[]
  prompt?: string
  files?: BaseFile[]
}

interface IAIPromptHistoryProps {
  baseFiles?: BaseFile[]
  getInvisibleHistory?: () => Promise<IAIPromptHistory[]>
  history: IAIPromptHistory[]
  prompt: string
  files: BaseFile[]
  filePath: string
  onUpdateHistory: (history: IAIPromptHistory[]) => void
  onUpdatePrompt: (prompt: string) => void
  onBlurPrompt?: () => void
  onUpdateFiles: (files: BaseFile[]) => void
  identifier: string
  promptOptions?: Record<string, any>
}

export const AIPromptHistory = ({
  baseFiles,
  getInvisibleHistory,
  history,
  prompt,
  files,
  filePath,
  onUpdateHistory,
  onUpdatePrompt,
  onBlurPrompt,
  onUpdateFiles,
  identifier,
  promptOptions,
}: IAIPromptHistoryProps) => {
  const loanGlobalSettings = useSelector((state: any) => state.loanDetail.loanGlobalSettings)
  const defaultSelection = resolveDefaultAiSelection(loanGlobalSettings)

  const [loading, setLoading] = useState(false)
  const [engineType, setEngineType] = useState<AiEngine>(defaultSelection.engine)
  const [modelType, setModelType] = useState(defaultSelection.model)
  const { hasPermission } = usePermissions()

  const canChangeModel = hasPermission('Global.Ai.Model.Change')

  useEffect(() => {
    setEngineType(defaultSelection.engine)
    setModelType(defaultSelection.model)
  }, [defaultSelection.engine, defaultSelection.model])

  const modelOptions = useMemo(() => getAiModelOptionsForEngine(engineType), [engineType])

  useEffect(() => {
    if (!getAiModelsForEngine(engineType)[modelType]) {
      setModelType(modelOptions[0]?.value || defaultSelection.model)
    }
  }, [engineType, modelOptions, modelType, defaultSelection.model])

  const getAiOptions = () => ({
    ...(promptOptions || {}),
    ...(canChangeModel ? { engine: engineType, model: modelType } : {}),
  })

  const onSendPrompt = async () => {
    prompt = (prompt || '').trim()
    if (!prompt) return
    setLoading(true)
    const invisibleHistory = getInvisibleHistory ? await getInvisibleHistory() : []
    const { data: response } = await postAiPrompt(identifier, 'prompt', {
      prompt,
      files: files || [],
      baseFiles: baseFiles || [],
      history: [...(invisibleHistory || []), ...(history || [])],
      options: getAiOptions(),
    })
    setLoading(false)

    const newHistory: IAIPromptHistory[] = [
      ...(history || []),
      { role: 'user', text: prompt, files },
      { role: 'model', text: response },
    ]
    onUpdateHistory(newHistory)
  }

  const onHighlightHistory = async (hIndex: number) => {
    const newHistory: IAIPromptHistory[] = [...(history || [])]
    newHistory[hIndex].highlight = !newHistory[hIndex].highlight
    onUpdateHistory(newHistory)

    const { highlight } = newHistory[hIndex]
    const invisibleHistory = getInvisibleHistory ? await getInvisibleHistory() : []

    postAiPrompt(identifier, 'highlight', {
      highlight,
      files: files || [],
      baseFiles: baseFiles || [],
      history: highlight ? [...(invisibleHistory || []), ...(history.slice(0, hIndex + 1) || [])] : [],
      options: getAiOptions(),
    })
  }

  const onRemoveHistory = async (hIndex: number) => {
    const content = (
      <div className="text-gray-600 mb-4 text-md">Are you sure you want to delete this prompt and its response?</div>
    )
    const result = await confirm(content)
    if (!result) return

    const newHistory: IAIPromptHistory[] = [...(history || [])]
    const removeCount = hIndex + 1 < newHistory.length && newHistory[hIndex + 1].role === 'model' ? 2 : 1
    newHistory.splice(hIndex, removeCount)
    onUpdateHistory(newHistory)
  }

  return (
    <div className="relative my-2 pl-4">
      <LayoutLoading show={!!loading} />
      {history?.map((history: IAIPromptHistory, hIndex: number) => (
        <div
          key={`history-${hIndex}`}
          className={`text-sm flex items-start gap-2 hover:bg-gray-100 ${history.highlight ? 'bg-yellow-50' : ''}`}
        >
          {history.role == 'user' ? <UserIcon className="w-4 h-4" /> : <ComputerDesktopIcon className="w-4 h-4" />}
          <AiTextRender text={history.text} className="flex-1" as="p" />
          <FileLinks files={history.files} />
          {history.role === 'user' && (
            <>
              <Tooltip message={history.highlight ? 'AI is in learning mode.' : 'Teach AI what it did wrong.'}>
                <button
                  className="transition-opacity text-shade-blue cursor-pointer"
                  onClick={() => onHighlightHistory(hIndex)}
                >
                  <AcademicCapIcon className="w-4 h-4" />
                </button>
              </Tooltip>

              <Tooltip message="Delete this prompt and response">
                <button
                  className="transition-opacity text-red-600 hover:text-red-800 cursor-pointer"
                  onClick={() => onRemoveHistory(hIndex)}
                >
                  <MinusCircleIcon className="w-4 h-4" />
                </button>
              </Tooltip>
            </>
          )}
        </div>
      ))}

      <div className="flex items-center gap-2">
        <div className="flex-1">
          <TextArea
            rows={1}
            additionalElements={
              <>
                {IS_COMPANY_KIRK && canChangeModel && (
                  <>
                    <select
                      onChange={(e) => setEngineType(e.target.value as AiEngine)}
                      value={engineType}
                      className="block rounded py-0 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                    >
                      {AiEngineOptions.map(({ name, value }) => (
                        <option key={value} value={value}>
                          {name}
                        </option>
                      ))}
                    </select>
                    <select
                      onChange={(e) => setModelType(e.target.value)}
                      value={modelType}
                      className="block rounded py-0 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                    >
                      {modelOptions.map(({ name, value }) => (
                        <option key={value} value={value}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <InputFileAddon filePath={filePath} value={files || []} onChange={(value) => onUpdateFiles(value)} />
              </>
            }
            title="Prompt to AI"
            value={prompt || ''}
            onChange={(value) => onUpdatePrompt(value)}
            onBlur={() => (onBlurPrompt ? onBlurPrompt() : null)}
          />
        </div>
        <span className="btn-icon" onClick={() => onSendPrompt()}>
          <PaperAirplaneIcon className="w-4 h-4" />
        </span>
      </div>
    </div>
  )
}
